import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { AlertCircle, Loader2 } from 'lucide-react';
import api from '../lib/api';

// v4.37.1 item 6: only ever return to a same-site absolute path. Reject schemes,
// hosts, protocol-relative '//', backslashes and auth pages (open-redirect safe).
const safeReturnPath = (v) => {
  if (!v || typeof v !== 'string') return null;
  const s = v.trim();
  if (!s || s.length > 512) return null;
  if (!s.startsWith('/') || s.startsWith('//') || s.startsWith('/\\')) return null;
  if (s.includes('\\') || s.includes('://')) return null;
  if (/[\u0000-\u001f\s]/.test(s)) return null;
  const low = s.toLowerCase();
  if (low.startsWith('/login') || low.startsWith('/logged-out') || low.startsWith('/api/')) return null;
  return s;
};
const peekStashedReturn = () => {
  try { return sessionStorage.getItem('postLoginReturnTo'); } catch { return null; }
};
const takeStashedReturn = () => {
  try {
    const v = sessionStorage.getItem('postLoginReturnTo');
    sessionStorage.removeItem('postLoginReturnTo');
    return v;
  } catch { return null; }
};

const SSO_ERROR_MESSAGES = {
  incomplete: 'Sign-in with Microsoft did not complete. Please try again.',
  missing_state: 'Your session expired before returning from Microsoft. Please try again.',
  invalid_state: 'The sign-in session was tampered with or expired. Please try again.',
  state_mismatch: 'Sign-in security check failed. Please try again from the login page.',
  no_id_token: 'Microsoft did not return an identity token. Contact your administrator.',
  no_email: 'Your Microsoft account does not have an email on file. Contact your administrator.',
  not_provisioned: 'Your Microsoft account is not yet provisioned in TriCorps 360. Contact your administrator.',
  oid_conflict: 'This email is already linked to a different Microsoft account. Contact your administrator.',
  account_disabled: 'Your account is disabled. Contact your administrator.',
  account_locked: 'Your account is locked. Contact your administrator.',
  account_deactivated: 'Your account has been deactivated.',
};

/**
 * Employee-facing login page. Deliberately single-action:
 *   → one button, "Sign in with Microsoft"
 *
 * The password-based login form has been moved to /emergency-access and is
 * only used when Microsoft SSO is unavailable (see BREAK_GLASS_HOWTO.md).
 * This eliminates decision paralysis for the 12-person workforce.
 */
export default function Login() {
  const [error, setError] = useState('');
  // 'checking' until the backend answers; 'configured'/'unconfigured' only when
  // the server actually responds; 'updating' when the server did NOT answer
  // (network error / timeout / 5xx — e.g. during a deploy traffic switch), in
  // which case we keep retrying instead of falsely claiming "not configured".
  const [configStatus, setConfigStatus] = useState('checking');
  const [ssoLoading, setSsoLoading] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  // Align the background so Devon Tower's centerline sits exactly at the
  // horizontal centre of the window at every width (the sign-in card is itself
  // window-centred, so card centre and tower centreline line up). Recomputed on
  // resize. Tower centreline measured at x≈860/1672 = 0.514 of the image width.
  const [bgStyle, setBgStyle] = useState({});
  useEffect(() => {
    const IMG_W = 1672, IMG_H = 752, TOWER_FRAC = 0.514;
    const place = () => {
      const W = window.innerWidth, H = window.innerHeight;
      const scale = Math.max(W / IMG_W, H / IMG_H); // CSS "cover"
      const sw = IMG_W * scale, sh = IMG_H * scale;
      const leftPx = W / 2 - TOWER_FRAC * sw; // tower lands at window centre
      setBgStyle({
        backgroundImage: "url('/login-background-clean.png')",
        backgroundRepeat: 'no-repeat',
        backgroundSize: `${sw}px ${sh}px`,
        backgroundPosition: `${leftPx}px center`,
      });
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, []);

  useEffect(() => {
    let cancelled = false;
    let timer = null;
    let attempt = 0;
    const check = async () => {
      try {
        const r = await api.get('/auth/oidc/status', { timeout: 8000 });
        if (cancelled) return;
        // Server answered: trust it. configured:false => truly not configured.
        setConfigStatus(r.data?.configured ? 'configured' : 'unconfigured');
      } catch (e) {
        if (cancelled) return;
        // Server did NOT answer (no response, timeout, or 5xx). This is the
        // deploy/updating window — never show "not configured"; keep retrying.
        setConfigStatus('updating');
        attempt += 1;
        // Retry the FIRST failure fast (1s) so a single cold-start / traffic-switch
        // blip clears in ~1s instead of stranding the user on "updating"; then back
        // off linearly (2s, 3s …) capped at 8s for a genuinely-down backend.
        const delay = Math.min(1000 * attempt, 8000);
        timer = setTimeout(check, delay);
      }
    };
    check();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, []);

  // Handle the OIDC callback landing on /login?sso=success — read the httpOnly
  // handoff cookie via POST /auth/oidc/complete and log the user in.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ssoResult = params.get('sso');
    const ssoError = params.get('sso_error');

    // 2026-08-23: idle-timeout / revoked-session landings surface as a
    // friendly banner instead of a scary "Session expired" toast. The axios
    // interceptor sets ?reason=idle|revoked before bouncing us here.
    const reason = params.get('reason');
    if (reason === 'idle') {
      setError('You were signed out after 30 minutes of inactivity. Please sign in again to continue.');
      window.history.replaceState({}, '', '/login');
      return;
    }
    if (reason === 'revoked') {
      setError('Your session was ended. Please sign in again.');
      window.history.replaceState({}, '', '/login');
      return;
    }

    if (ssoError) {
      // Map known SSO errors to friendly copy; fall back to the raw code so we
      // can still diagnose new/rare failures (e.g. exchange_AADSTS7000215 =
      // expired client secret in Entra).
      const friendly = SSO_ERROR_MESSAGES[ssoError];
      if (friendly) {
        setError(friendly);
      } else if (ssoError.startsWith('exchange_')) {
        const tag = ssoError.replace('exchange_', '');
        setError(`Microsoft rejected the sign-in (code: ${tag}). Please contact your administrator with this code.`);
      } else {
        setError(`Sign-in failed (${ssoError}). Please try again.`);
      }
      window.history.replaceState({}, '', '/login');
      return;
    }

    if (ssoResult === 'success') {
      const handoffCode = params.get('code');
      // Snapshot BEFORE the call: the axios 401 interceptor clears localStorage,
      // so checking it inside .catch() would always come back empty.
      const alreadySignedIn = !!(localStorage.getItem('token') && localStorage.getItem('user'));
      // Strip the query string BEFORE anything else: the code is a one-time
      // bearer credential, and clearing it means a refresh can never re-fire
      // this effect against an already-redeemed code.
      window.history.replaceState({}, '', '/login');
      setSsoLoading(true);
      api.post('/auth/oidc/complete', handoffCode ? { code: handoffCode } : {}, { withCredentials: true })
        .then((res) => {
          const { access_token, user: userData, return_to } = res.data;
          localStorage.setItem('token', access_token);
          localStorage.setItem('user', JSON.stringify(userData));
          setUser(userData);
          // Prefer the server-validated same-site deep link; fall back to the
          // path we stashed before bouncing to SSO; else the dashboard.
          let dest = safeReturnPath(return_to) || safeReturnPath(takeStashedReturn()) || '/';
          navigate(dest);
        })
        .catch((err) => {
          // Already signed in on this browser? Don't dead-end the user on a
          // handoff hiccup — send them back in on the session they still have.
          if (alreadySignedIn) {
            navigate('/');
            return;
          }
          setError(err.response?.data?.detail
            || 'We could not finish signing you in. Please press Sign in with Microsoft to try again.');
          setSsoLoading(false);
        });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMicrosoftSignIn = () => {
    // Same-origin on purpose: the ingress routes /api to the backend, so the SSO
    // handshake always starts on whatever domain the user is actually on
    // (preview, tricorps360.com, or a future custom domain).
    // v4.37.1 item 6: forward a validated same-site deep link so a signed-out
    // user who opened an email link lands back on that exact page after sign-in.
    let url = `${window.location.origin}/api/auth/oidc/login`;
    const rt = safeReturnPath(peekStashedReturn());
    if (rt) url += `?return_to=${encodeURIComponent(rt)}`;
    window.location.assign(url);
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-start px-4 pt-[34vh] md:pt-[40vh] pb-16 bg-black relative"
      style={{ ...bgStyle, fontFamily: "'Open Sans', sans-serif" }}
      data-testid="login-page"
    >
      {/* Light scrim only at the very bottom so Devon Tower stays fully visible
          above the card; the card itself carries its own dark tint. */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/55" aria-hidden="true" />

      {/* Option B (mockup): dark, semi-transparent card so the skyline shows
          through. White heading, light-gray sub-copy, white logo, a solid white
          button with TriCorps Blue text, and a white underlined help-desk email. */}
      <div className="relative w-full" style={{ maxWidth: '21rem' }}>
        <Card className="border-white/15 shadow-2xl bg-black/60 backdrop-blur-md">
          <CardHeader className="space-y-3 pb-3 pt-6">
            <div className="flex justify-center">
              <img
                src="/tricorps360-horizontal-white.png"
                alt="TriCorps 360"
                className="w-full object-contain"
                style={{ maxWidth: '12rem' }}
                data-testid="login-brand-logo"
              />
            </div>
            <div className="space-y-1">
              <CardTitle className="text-xl font-bold text-center text-white">Secure Sign In</CardTitle>
              <CardDescription className="text-center text-sm" style={{ color: '#E2E8F0' }}>
                {ssoLoading ? 'Completing Microsoft sign-in...' : 'Sign in with your authorized TriCorps Microsoft account to continue.'}
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent className="pb-6">
            {error && (
              <Alert variant="destructive" className="bg-red-50 border-red-200 mb-4" data-testid="login-error-alert">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {configStatus === 'configured' && (
              <Button
                type="button"
                className="w-full h-11 text-sm font-semibold bg-white hover:bg-slate-100"
                style={{ color: '#002173' }}
                onClick={handleMicrosoftSignIn}
                disabled={ssoLoading}
                data-testid="sso-microsoft-btn"
              >
                <svg aria-hidden="true" className="mr-2 h-5 w-5" viewBox="0 0 24 24">
                  <path fill="#f25022" d="M1 1h10v10H1z" />
                  <path fill="#7fba00" d="M13 1h10v10H13z" />
                  <path fill="#00a4ef" d="M1 13h10v10H1z" />
                  <path fill="#ffb900" d="M13 13h10v10H13z" />
                </svg>
                {ssoLoading ? 'Signing in...' : 'Sign in with Microsoft'}
              </Button>
            )}

            {(configStatus === 'checking' || configStatus === 'updating') && (
              <Alert className="bg-blue-50 border-blue-200" data-testid="login-updating-alert">
                <Loader2 className="h-4 w-4 animate-spin" />
                <AlertDescription>
                  TriCorps 360 is updating. This usually takes a few minutes. Retrying&hellip;
                </AlertDescription>
              </Alert>
            )}

            {configStatus === 'unconfigured' && (
              <Alert className="bg-amber-50 border-amber-200" data-testid="login-not-configured-alert">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Microsoft sign-in is not yet configured. Please contact your administrator.
                </AlertDescription>
              </Alert>
            )}

            <div className="mt-5 text-center text-xs" style={{ color: '#E2E8F0' }}>
              Trouble signing in? Email&nbsp;
              <a
                href="mailto:helpdesk@tricorps.com"
                className="text-white underline font-semibold hover:text-white/90"
                data-testid="login-helpdesk-link"
              >
                helpdesk@tricorps.com
              </a>
            </div>
          </CardContent>
        </Card>

        <p
          className="mt-7 px-4 text-center text-base md:text-lg font-light tracking-wide text-white drop-shadow"
          data-testid="login-tagline"
        >
          A world-class blend of{' '}
          <span className="font-semibold text-white">security</span>{' '}and{' '}
          <span className="font-semibold text-white">technology</span>.
        </p>
      </div>
    </div>
  );
}
